import {
	PageInfo,
	Contact,
	useUpdateContactDeleteMutation,
	ContactType,
	useGetAllContactsWithTypesLazyQuery,
} from "@repo/server-api";
import { useNotification } from '@repo/shared/components';
import { t } from "i18next";
import { useState } from "react";

import { setContactValues } from "@repo/legacy/components/helper/Index";
import { DataSource } from "@repo/legacy/components/shared/lists/DataSource";
import { PaginationModel } from "@repo/legacy/components/shared/lists/PaginationModel";

import usePersistentState from "../../../store/usePersistentState";

export function useAllContactsFullDataSource(): DataSource<Contact> {
	const [rows, setRows] = useState<Contact[]>([]);
	const [rowCount, setRowCount] = useState(0);
	const [isLoading, setIsLoading] = useState(false);
	const [, setPageInfo] = useState<PageInfo | null>(null);
	const [searchBy, setSearchBy] = useState<string>("");
	const [types, setTypes] = usePersistentState("contactTypes", [] as ContactType[]);
	const [showDeleted, setShowDeleted] = useState<boolean>(false);
	const [pageModal, setPageModal] = useState<PaginationModel>({
		page: 0,
		pageSize: 10,
	});

	const { showNotification } = useNotification();

	const [deleteContact] = useUpdateContactDeleteMutation();

	const [getContacts] = useGetAllContactsWithTypesLazyQuery({
		fetchPolicy: "no-cache",
	});

	const fetchDataWithType = (
		page: PaginationModel,
		s: string,
		t: ContactType[],
		d: boolean = false,
	) => {
		setIsLoading(true);
		setTypes(t);
		setSearchBy(s);
		setShowDeleted(d);
		setPageModal(page);
		getContacts({
			variables: {
				first: page.pageSize,
				after: (page.pageSize * page.page).toString(),
				name: s,
				types: t,
				showDeleted: d,
			},
		}).then((res) => {
			if (res.data && res.data.allContacts?.nodes) {
				const tmp = res.data.allContacts.nodes.map((node) => ({
					...setContactValues(node as unknown as Contact),
					...(node as unknown as Contact),
				}));
				if (tmp.length === 0 && page.page > 0) {
					fetchDataWithType({ page: page.page - 1, pageSize: page.pageSize }, s, t, d);
					return;
				}
				setRows(tmp);
				setRowCount(res.data.allContacts.totalCount);
				setPageInfo(res.data.allContacts.pageInfo);
				setIsLoading(false);
			}
		});
	};

	const deleteData = (id: string, value?: boolean) => {
		deleteContact({
			variables: { id, isDeleted: typeof value === "boolean" ? value : true },
		}).then((res) => {
			if (res.data && res.data.updateContactDelete?.boolean) {
				fetchDataWithType(pageModal, searchBy, types, showDeleted);
				if (value === true) showNotification(t("contacts.contactDeleted"), "success");
				else showNotification(t("contacts.contactRestored"), "success");
			}
		});
	};

	const refresh = () => {
		fetchDataWithType(pageModal, searchBy, types, showDeleted);
	};

	return {
		rows,
		rowCount,
		isLoading,
		fetchDataWithType,
		deleteData,
		refresh,
		showDeleted,
		setShowDeleted,
	};
}
